import { defineStore } from 'pinia';
import type {
  MyCompany,
  UpdateMe,
  PatchMyCompanySchema,
  CompanyLogoSchema,
} from 'src/api';
import { CompanyApi } from 'src/api';
import type { Errors } from 'src/utils/apiErrors';
import { useUser } from 'stores/user/useUser';
import { Notify } from 'quasar';
import gettext from 'src/utils/gettext';

const { $gettext } = gettext;

export interface CompanyState {
  company: MyCompany | null;
  companyLogo: CompanyLogoSchema | null;
  errors: Errors;
}

export const useProfileDetail = defineStore('useProfileDetail', {
  state: () => {
    return {
      company: null,
      companyLogo: null,
      errors: {},
    } as CompanyState;
  },
  actions: {
    async getUserAndCompany() {
      await CompanyApi.companiesGetMyCompanyGet().then((r: MyCompany) => {
        this.company = r;
      });
    },

    async updateOnlyUser(updateUserObj: UpdateMe) {
      const userStore = useUser();
      userStore.errors = {};
      await userStore.updateUser(updateUserObj);
      if (Object.keys(userStore.errors).length !== 0) {
        this.errors = userStore.errors;
        return;
      }
    },

    async patchCompany(patchCompany: PatchMyCompanySchema) {
      await CompanyApi.companiesPatchMyCompanyPatch({
        requestBody: patchCompany,
      })
        .then((r: MyCompany) => {
          this.errors = {};
          this.company = r;
          Notify.create({
            color: 'positive',
            message: $gettext('Uloženo'),
            icon: 'fa-solid fa-circle-check',
            timeout: 1000,
          });
        })
        .catchApiErrors((err: Errors) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 1000,
            });
          });
        });
    },
    async uploadLogo(file: Blob) {
      await CompanyApi.companiesUploadMyCompanyLogoPost({
        formData: { image: file },
      })
        .then(() => {
          this.errors = {};
        })
        .catchApiErrors((err: Errors) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
            });
          });
        });
    },
    async removeLogo() {
      await CompanyApi.companiesDeleteMyCompanyLogoDelete()
        .then(() => {
          this.errors = {};
        })
        .catchApiErrors((err: Errors) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
            });
          });
        });
    },
    async uploadSignature(file: Blob) {
      await CompanyApi.companiesUploadMyCompanySignaturePost({
        formData: { image: file },
      })
        .then(() => {
          this.errors = {};
        })
        .catchApiErrors((err: Errors) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
            });
          });
        });
    },
    async removeSignature() {
      await CompanyApi.companiesDeleteMyCompanySignatureDelete()
        .then(() => {
          this.errors = {};
        })
        .catchApiErrors((err: Errors) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
            });
          });
        });
    },
  },
  getters: {
    companyBankAccount(): string | undefined {
      if (
        this.company &&
        this.company.bank_account_number &&
        this.company.bank_code
      ) {
        return `${this.company.bank_account_number}/${this.company.bank_code}`;
      }
      return undefined;
    },
  },
});
